import { debounce } from '../../../scripts/utils';

if (!customElements.get('stories-gallery')) {
  class StoriesGallery extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.autoAdvanceTimer = null;
      this.defaultStoryDuration = 8000; // TODO: Make it dynamic based on theme settings
      this.currentStoryDuration = this.defaultStoryDuration;
      this.touchStartX = 0;
      this.touchEndX = 0;
      this.activeItem = null;
      this._initElements();
      if(!this.closeButton || !this.buttonPrev || !this.buttonNext || !this.galleryItemsWrapper) return;
      if(!this.galleryItemsWrapper || !this.progressWrapper) return;
      if(!this.templates.gallery_item || !this.templates.progress_segment) return;

      this.teleportGalleryToTop();
      this.setCustomerActions();
      this.setupTouchEvents();
      this.startActions();
    }

    // DEFAULT METHODS 
    _initElements() {
      this.galleryItems = [];
      this.galleryItemsWrapper = this.querySelector('.stories-gallery__items');
      this.progressWrapper = this.querySelector('.stories-gallery__progress');
      this.progressBars = [];
      this.buttonPrev = this.querySelector('.stories-gallery__navigation--prev');
      this.buttonNext = this.querySelector('.stories-gallery__navigation--next');
      this.closeButton = this.querySelector('.stories-gallery__close');

      this.templates = {
        gallery_item: document.querySelector('template.stories-gallery__item'),
        progress_segment: document.querySelector('template#progress_segment')
      }
    }
    teleportGalleryToTop() {
      const mainContent = document.querySelector('main#main');
      if (!mainContent ) {
          console.error("Cannot teleport gallery: main#main or StoriesGallery element not found.");
          return;
      }
      mainContent.insertAdjacentElement('afterend', this);
    }

    // EVENTS 
    setCustomerActions() {
      if (window.innerWidth > 768) { 
        this.buttonPrev.addEventListener('click', this.onPrevClick.bind(this));
        this.buttonNext.addEventListener('click', this.onNextClick.bind(this));
      } else {
        if (this.buttonPrev) this.buttonPrev.style.display = 'none';
        if (this.buttonNext) this.buttonNext.style.display = 'none';
        
        this.galleryItemsWrapper.style.position = 'relative';
        this.galleryItemsWrapper.style.cursor = 'pointer';
        this.galleryItemsWrapper.addEventListener('click', this.handleGalleryClick.bind(this));
      }
      
      this.closeButton.addEventListener('click', this.onCloseButtonClicked.bind(this));
      
      this.addEventListener('click', (event) => {
        if (event.target === this) {
          this.onCloseButtonClicked();
        }
      });
    }

    setupTouchEvents() {
      this.galleryItemsWrapper.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
      this.galleryItemsWrapper.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
      this.galleryItemsWrapper.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });
      
      this.debouncedResizeHandler = debounce(this.handleResizeCheck.bind(this), 50);
      window.addEventListener('resize', this.debouncedResizeHandler);
    }
    
    handleResizeCheck() {
      if (window.innerWidth <= 768) {
        if (this.buttonPrev) this.buttonPrev.style.display = 'none';
        if (this.buttonNext) this.buttonNext.style.display = 'none';
      } else {
        if (this.buttonPrev) this.buttonPrev.style.display = '';
        if (this.buttonNext) this.buttonNext.style.display = '';
      }
    }
    
    handleGalleryClick(event) {
      if (window.innerWidth > 768) return;
      const clickX = event.clientX;
      const galleryRect = this.galleryItemsWrapper.getBoundingClientRect();
      const clickZoneWidth = 60;
      if (clickX - galleryRect.left < clickZoneWidth) {
        this.onPrevClick(true);
      } else if (galleryRect.right - clickX < clickZoneWidth) {
        this.onNextClick(true);
      }
    }

    handleTouchStart(event) {
      this.touchStartX = event.touches[0].clientX;
      this.touchEndX = this.touchStartX;
    }

    handleTouchMove(event) {
      if (event.touches.length > 1) return; 
      this.touchEndX = event.touches[0].clientX;
      const xDiff = Math.abs(this.touchStartX - this.touchEndX);
      const yDiff = Math.abs(event.touches[0].clientY - this.touchStartY);
      if (xDiff > yDiff) {
        event.preventDefault();
      }
    }

    handleTouchEnd() {
      const minSwipeDistance = 50; 
      const difference = this.touchStartX - this.touchEndX;
      if (Math.abs(difference) > minSwipeDistance) {
        if (difference > 0) {
          this.onNextClick(true);
        } else {
          this.onPrevClick(true);
        }
      }
    }


    listenToAllStoriesCompleted() {
      document.addEventListener('all-stories-completed', () => {
        this.onCloseButtonClicked();
      });
    }

    startActions(){
      this.currentIndex = this.getCurrentIndex();
      this.updateNavigationButtons();
      this.startAutoAdvance();
      this.listenToAllStoriesCompleted();
    }

    openGallery() {
      this.classList.add('active');
      this.onToggle(true);
    }

    onCloseButtonClicked() {
      this.classList.remove('active');
      this.onToggle(false);
      this.destructor();
    }

    // HELPERS 
    getCurrentIndex() {
      const activeItem = this.galleryItems.find(item => item.classList.contains('active'));
      if(activeItem) {
        this.currentIndex = parseInt(activeItem.dataset.storyIndex) || 0;
      }
      return this.currentIndex;
    }

    currentItemDuration() {
      const activeItem = this.galleryItems.find(item => item.classList.contains('active'));
      this.activeItem = activeItem;
      if(activeItem) return parseInt(activeItem.dataset.storyDuration) || this.defaultStoryDuration;
      return this.defaultStoryDuration;
    }

    destructor() {
      if (this.galleryItemsWrapper) {
        this.galleryItemsWrapper.innerHTML = '';
        this.galleryItemsWrapper.style.position = '';
        this.galleryItemsWrapper.style.cursor = '';
      }
      if (this.progressWrapper) this.progressWrapper.innerHTML = '';
      
      // Clean up event listeners
      window.removeEventListener('resize', this.handleResize);
      
      this.progressBars = [];
      this.galleryItems = [];
    }

    updateNavigationButtons() {
      if (!this.buttonPrev || !this.buttonNext) return;
      
      if (this.currentIndex === 0) {
        this.buttonPrev.style.opacity = '0.5';
        this.buttonPrev.style.pointerEvents = 'none';
      } else {
        this.buttonPrev.style.opacity = '1';
        this.buttonPrev.style.pointerEvents = 'auto';
      }
      
      if (this.currentIndex === this.galleryItems.length - 1) {
        this.buttonNext.style.opacity = '0.5';
        this.buttonNext.style.pointerEvents = 'none';
        this.buttonNext.cl
      } else {
        this.buttonNext.style.opacity = '1';
        this.buttonNext.style.pointerEvents = 'auto';
      }
    }

    onToggle(open = true) {
      if (open) {
        document.documentElement.setAttribute('scroll-lock', '');
      } else {
        document.documentElement.removeAttribute('scroll-lock');
      }
    }

    // GALLERY CONTROLS
    onPrevClick(isMobile = false) {
      if (this.currentIndex > 0) {
        this.goToSlide(this.currentIndex - 1, true, isMobile);
      }
    }

    onNextClick(isMobile = false) {
      if (this.currentIndex < this.galleryItems.length - 1) {
        this.goToSlide(this.currentIndex + 1, true, isMobile);
      } else if (isMobile && window.innerWidth <= 768) {
        this.onCloseButtonClicked();
      }
    }

    goToSlide(index, isManual = false) {
      if (this.galleryItems[this.currentIndex]) {
        this.galleryItems[this.currentIndex].classList.remove('active');
      }
      this.currentIndex = index;
      if (this.galleryItems[this.currentIndex]) {
        this.galleryItems[this.currentIndex].classList.add('active');
      }
      this.currentStoryDuration = this.currentItemDuration();
      this.updateNavigationButtons();
      this.updateProgressBars();
      
      if (isManual) {
        this.stopAutoAdvance();
        this.startAutoAdvance();
      }
    }

    startAutoAdvance() {
      let isStoryVideo = false;
      if(this.activeItem) {
        isStoryVideo = this.activeItem.dataset.isStoryVideo === 'true';
        if(isStoryVideo) this.runStoryVideo();
      }
      this.stopAutoAdvance();
      this.autoAdvanceTimer = setTimeout(() => {
        if (this.currentIndex < this.galleryItems.length - 1) {
          this.goToSlide(this.currentIndex + 1, false);
          this.startAutoAdvance();
        }
      }, this.currentStoryDuration);
    }

    runStoryVideo() {
      const video = this.activeItem.querySelector('video');
      if(!video) return;
      video.play();
    }
    stopAutoAdvance() {
      if (this.autoAdvanceTimer) {
        clearTimeout(this.autoAdvanceTimer);
        this.autoAdvanceTimer = null;
      }
    }

    // PROGRESS BAR
    initProgressBars() {
      const progressBarTemplate = this.templates.progress_segment;
      if(!progressBarTemplate) return;
      const progressBarTemplateContent = progressBarTemplate.content.cloneNode(true);
      if(this.galleryItems.length == 0) return;
      for(let i = 0; i < this.galleryItems.length; i++) {
        const progressBar = progressBarTemplateContent.cloneNode(true);
        this.progressWrapper.appendChild(progressBar);
      }
      this.progressBars = Array.from(this.querySelectorAll('.stories-gallery__progress-bar'));
      if(this.progressBars.length > 0) this.updateProgressBars();
    }

  
    handleProgressBarAnimationEnd(bar) {
      if(!bar) return;
      bar.target.classList.add('completed');
      bar.target.removeEventListener('animationend', this.boundHandleAnimationEnd);
      
      // Check if all stories are completed
      const completedStories = this.progressBars.filter(bar => bar.classList.contains('completed'));
      if (completedStories.length === this.progressBars.length) {
        document.dispatchEvent(new CustomEvent('all-stories-completed'));
      }
    }

    updateProgressBars() {
      if (!this.progressBars) return;
      
      // Bind the handler to the instance
      this.boundHandleAnimationEnd = this.handleProgressBarAnimationEnd.bind(this);
      
      this.progressBars.forEach((bar, index) => {
        bar.classList.remove('active-progress-bar', 'completed');
        
        if (index < this.currentIndex) {
          bar.classList.add('completed');
          bar.style.animation = 'none';
        } else if (index === this.currentIndex) {
          bar.classList.add('active-progress-bar');
          if (bar.classList.contains('active-progress-bar')) {
            bar.style.animation = `progress-fill ${this.currentStoryDuration}ms linear forwards`;
            bar.addEventListener('animationend', this.boundHandleAnimationEnd);
          }
        } else if (index > this.currentIndex) {
          bar.style.animation = 'none';
        }
      });
    }

    // GALLERY ITEMS - build and render
    buildGalleryItems(targetElement) {
      if(!targetElement) {
        console.warn("can not build gallery items - targetElement not found"); 
        return false; 
      }
      let stories = [];
      try {
        stories = JSON.parse(targetElement.textContent);
      } catch (error) {
        console.warn("can not build gallery items - error", error);
        return false;
      }
      if(stories.length === 0) { 
        console.warn("can not build gallery items - stories array is empty"); 
        return false;
      }

      this.renderGalleryItems(stories);
      return true;
    }

    renderGalleryItems(stories) {
      const galleryItemTemplate = this.templates.gallery_item;
      if(!galleryItemTemplate) return;
      const galleryItemTemplateContent = galleryItemTemplate.content.cloneNode(true);
      for(let i = 0; i < stories.length; i++) {
        this.renderGalleryItem(stories[i], galleryItemTemplateContent, i);
      }
    }

    renderGalleryItem(story, galleryItemTemplateContent, index) {
      const mediaType = story.media_type;
      const {galleryItemElemets, galleryItem} = this.setGalleryItemElements(story, galleryItemTemplateContent, index);
      if(!galleryItemElemets || !galleryItem) return;
      if(mediaType === 'Image' && !galleryItemElemets.image) return;
      if(mediaType === 'Video' && !galleryItemElemets.video) return;

      if(mediaType === 'Image') {
        galleryItemElemets.image.src = story.media;
        galleryItemElemets.image.classList.remove('hidden');
      } else if(mediaType === 'Video') {
        galleryItemElemets.video.src = story.media;
        galleryItemElemets.video.classList.remove('hidden');
      }

      if(galleryItemElemets.captionLink) galleryItemElemets.captionLink.href = story.page_redirection;
      if(galleryItemElemets.captionImage) { 
        galleryItemElemets.captionImage.src = story.caption_image;
        if(galleryItemElemets.captionImage.src != '') galleryItemElemets.captionImage.classList.remove('hidden');
      }
      if(galleryItemElemets.captionText) galleryItemElemets.captionText.textContent = story.caption_text;
      if(galleryItemElemets.caption) galleryItemElemets.caption.style.backgroundColor = story.caption_color;

      this.galleryItemsWrapper.appendChild(galleryItem);
      if(this.galleryItemsWrapper) this.galleryItems = Array.from(this.galleryItemsWrapper.children);
    }

    setGalleryItemElements(story, galleryItemTemplateContent, index) {
      const galleryItem = galleryItemTemplateContent.cloneNode(true);
      if(!galleryItem) return;
      const galleryItemInner = galleryItem.querySelector('.stories-gallery__item--inner');
      if(!galleryItemInner) return;
      const galleryItemElemets = {
        image: galleryItemInner.querySelector('[story-image]'),
        video: galleryItemInner.querySelector('[story-video]'),
        caption: galleryItemInner.querySelector('[story-caption]'),
        captionLink: galleryItemInner.querySelector('[story-caption-link]'),
        captionImage: galleryItemInner.querySelector('[story-caption-image]'),
        captionText: galleryItemInner.querySelector('[story-caption-text]')
      };
      galleryItemInner.dataset.storyIndex = index || 0;
      galleryItemInner.dataset.storyId = story.id || '';
      galleryItemInner.dataset.storyDuration = story.duration || 8000;
      galleryItemInner.dataset.isStoryVideo = story.media_type === 'Video' ? 'true' : 'false';
      if(!galleryItemElemets.caption || !galleryItemElemets.captionLink || !galleryItemElemets.captionImage || !galleryItemElemets.captionText) return;
      return {galleryItemElemets, galleryItem};

    }
  }
  customElements.define('stories-gallery', StoriesGallery);
}
export { StoriesGallery };